/**
 * Pugo Toolkit - the big test addon.
 *
 * Written to exercise every part of the addon API and prove the sandbox
 * walls hold, so a user can install it and see PASS or FAIL for each. The
 * same code is kept as addons/pugo-toolkit.addon.js in the repository, which
 * can be loaded with "Load from file".
 *
 * No backticks and no dollar-brace sequences below: the whole addon lives in a
 * template string.
 */

export const TOOLKIT_ADDON = String.raw`// @name Pugo Toolkit
// @author Pugo
// @description Self-test, notes, timer, dice, teams and a reaction game.

(function () {
  var root = pugo.root;
  var style = document.createElement('style');
  style.textContent = [
    '.tk{display:flex;flex-direction:column;gap:10px}',
    '.tabs{display:flex;flex-wrap:wrap;gap:4px;padding:4px;border-radius:12px;background:var(--bg-2,#151d2b)}',
    '.tab{flex:1;min-width:70px;border:0;border-radius:9px;padding:7px 8px;background:transparent;color:var(--text-dim,#a0a8b8);font-weight:600;transition:background .25s,color .25s}',
    '.tab.on{background:var(--accent,#6557f5);color:#fff}',
    '.card{border-radius:12px;background:var(--bg-2,#151d2b);padding:10px 12px}',
    '.row{display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid rgba(255,255,255,.06)}',
    '.row:last-child{border-bottom:0}',
    '.pill{flex:none;border-radius:999px;padding:2px 9px;font-size:11px;font-weight:800;letter-spacing:.04em}',
    '.pass{background:rgba(46,217,139,.16);color:var(--good,#2ed98b)}',
    '.fail{background:rgba(239,89,101,.16);color:var(--bad,#ef5965)}',
    '.wait{background:rgba(239,182,73,.16);color:var(--warn,#efb649)}',
    '.muted{opacity:.7;font-size:12px}',
    '.grow{flex:1;min-width:0}',
    '.big{font-size:30px;font-weight:800;letter-spacing:-.02em;font-variant-numeric:tabular-nums}',
    '.btn{border:0;border-radius:10px;padding:8px 12px;background:var(--bg-3,#1b2434);color:inherit;font-weight:600}',
    '.btn.primary{background:var(--accent,#6557f5);color:#fff}',
    'input,textarea{width:100%;border:0;border-radius:10px;padding:8px 10px;background:var(--bg-3,#1b2434);color:inherit;font:inherit;outline:none}',
    'textarea{resize:vertical;min-height:60px}',
    '.game{display:grid;place-items:center;height:120px;border-radius:12px;font-weight:800;font-size:18px;cursor:pointer;user-select:none;transition:background .2s}',
    '.bar{height:6px;border-radius:999px;background:var(--bg-3,#1b2434);overflow:hidden}',
    '.bar i{display:block;height:100%;background:var(--accent,#6557f5);transition:width .4s}'
  ].join('');
  document.head.appendChild(style);

  var TABS = ['Self-test', 'Notes', 'Timer', 'Dice & teams', 'Reaction'];
  var active = 'Self-test';
  var events = { route: 0 };
  var lastRoute = '';

  root.innerHTML = '<div class="tk"><div class="tabs" id="tabs"></div><div id="panel"></div></div>';
  var tabsEl = document.getElementById('tabs');
  var panel = document.getElementById('panel');

  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function fit() { setTimeout(function () { pugo.setHeight(Math.min(600, document.body.scrollHeight + 6)); }, 30); }
  function withTimeout(p, ms) {
    return Promise.race([p, new Promise(function (_, reject) { setTimeout(function () { reject(new Error('timed out')); }, ms); })]);
  }
  function load() { return withTimeout(pugo.storage.get(), 3000).then(function (d) { return d || {}; }); }
  function save(patch) { return load().then(function (d) { for (var k in patch) d[k] = patch[k]; return pugo.storage.set(d); }); }

  function drawTabs() {
    tabsEl.innerHTML = TABS.map(function (t) {
      return '<button class="tab' + (t === active ? ' on' : '') + '" data-tab="' + esc(t) + '">' + esc(t) + '</button>';
    }).join('');
    Array.prototype.forEach.call(tabsEl.querySelectorAll('button'), function (b) {
      b.onclick = function () { active = b.getAttribute('data-tab'); drawTabs(); draw(); };
    });
  }

  function draw() {
    if (active === 'Self-test') selfTest();
    else if (active === 'Notes') notes();
    else if (active === 'Timer') timer();
    else if (active === 'Dice & teams') dice();
    else reaction();
    fit();
  }

  /* ------------------------------------------------ self-test */

  var results = [];
  function selfTest() {
    results = [];
    render();
    function add(name, fn) {
      var r = { name: name, state: 'wait', note: 'running...' };
      results.push(r);
      render();
      Promise.resolve().then(fn).then(function (note) { r.state = 'pass'; r.note = note || 'ok'; render(); })
        .catch(function (err) { r.state = 'fail'; r.note = (err && err.message) || String(err); render(); });
    }

    add('The pugo API exists', function () {
      var need = ['root', 'me', 'route', 'storage', 'setTitle', 'setHeight', 'on', 'notify', 'theme'];
      var missing = need.filter(function (k) { return !(k in pugo); });
      if (missing.length) throw new Error('missing: ' + missing.join(', '));
      return need.length + ' functions';
    });
    add('Who you are - pugo.me()', function () {
      return withTimeout(pugo.me(), 3000).then(function (me) {
        if (!me || typeof me.username !== 'string') throw new Error('no user came back');
        if (typeof me.level !== 'number') throw new Error('level is not a number');
        return me.displayName + ' - level ' + me.level;
      });
    });
    add('Where you are - pugo.route()', function () {
      return withTimeout(pugo.route(), 3000).then(function (r) {
        if (typeof r !== 'string') throw new Error('route is not text');
        lastRoute = r;
        return 'on "' + r + '"';
      });
    });
    add('Saving and reading back - pugo.storage', function () {
      var probe = Math.random().toString(36).slice(2);
      return save({ probe: probe }).then(load).then(function (d) {
        if (d.probe !== probe) throw new Error('what came back was different');
        return 'round trip matched';
      });
    });
    add('Panel title - pugo.setTitle()', function () {
      return withTimeout(pugo.setTitle('Pugo Toolkit'), 3000).then(function (ok) { if (!ok) throw new Error('refused'); return 'title set'; });
    });
    add('Panel height - pugo.setHeight()', function () {
      return withTimeout(pugo.setHeight(320), 3000).then(function (ok) { if (!ok) throw new Error('refused'); return 'height set'; });
    });
    add('Notifications - pugo.notify()', function () {
      return withTimeout(pugo.notify('Pugo Toolkit self-test is running'), 3000).then(function (ok) {
        if (!ok) throw new Error('refused');
        return 'a toast should have appeared';
      });
    });
    add('Theme colours - pugo.theme()', function () {
      return withTimeout(pugo.theme(), 3000).then(function (t) {
        if (!t || !t.accent) throw new Error('no accent colour');
        return 'accent ' + t.accent;
      });
    });
    add('Sandbox: no access to your login storage', function () {
      try { window.localStorage.getItem('pugo.token'); } catch (e) { return 'blocked, as it should be'; }
      throw new Error('localStorage was readable');
    });
    add('Sandbox: no access to the app page', function () {
      try { var t = parent.document.title; throw new Error('could read the app: ' + t); } catch (e) {
        if (/could read/.test(e.message)) throw e;
        return 'blocked, as it should be';
      }
    });
    add('Sandbox: no internet access', function () {
      return withTimeout(fetch('https://example.com/'), 4000).then(function () {
        throw new Error('a request got out');
      }, function (e) {
        if (/got out/.test(e.message)) throw e;
        return 'blocked, as it should be';
      });
    });
    add('Live events - route changes', function () {
      return new Promise(function (resolve, reject) {
        var start = events.route;
        var waited = 0;
        var t = setInterval(function () {
          waited += 250;
          if (events.route > start) { clearInterval(t); resolve('got "' + lastRoute + '"'); }
          else if (waited > 60000) { clearInterval(t); reject(new Error('no route event in 60s')); }
        }, 250);
      });
    });
  }

  function render() {
    if (active !== 'Self-test') return;
    var passed = results.filter(function (r) { return r.state === 'pass'; }).length;
    var failed = results.filter(function (r) { return r.state === 'fail'; }).length;
    var waiting = results.length - passed - failed;
    var pct = results.length ? Math.round((passed / results.length) * 100) : 0;
    panel.innerHTML =
      '<div class="card">' +
        '<div style="display:flex;align-items:center;gap:10px">' +
          '<div class="grow"><div style="font-weight:800;font-size:15px">' + passed + ' of ' + results.length + ' checks passed</div>' +
          '<div class="muted">' + (failed ? failed + ' failed' : 'nothing failed') + (waiting ? ' - ' + waiting + ' still running' : '') + '</div></div>' +
          '<button class="btn" id="rerun">Run again</button>' +
        '</div>' +
        '<div class="bar" style="margin-top:8px"><i style="width:' + pct + '%"></i></div>' +
        (waiting && results.length && results[results.length - 1].state === 'wait'
          ? '<div class="muted" style="margin-top:8px">The last check waits for a live event: go to another page in Pugo (for example Friends) and come back.</div>' : '') +
      '</div>' +
      '<div class="card" style="margin-top:8px">' + results.map(function (r) {
        return '<div class="row"><span class="pill ' + r.state + '">' + (r.state === 'wait' ? 'WAIT' : r.state.toUpperCase()) + '</span>' +
          '<div class="grow"><div style="font-weight:600">' + esc(r.name) + '</div><div class="muted">' + esc(r.note) + '</div></div></div>';
      }).join('') + '</div>';
    var b = document.getElementById('rerun');
    if (b) b.onclick = selfTest;
    fit();
  }

  /* ------------------------------------------------ notes */

  function notes() {
    load().then(function (d) {
      var list = d.notes || [];
      panel.innerHTML =
        '<div class="card"><div style="display:flex;gap:6px"><input id="note" placeholder="Write a note and press Enter" maxlength="200">' +
        '<button class="btn primary" id="addNote">Add</button></div>' +
        '<div id="list" style="margin-top:8px">' + (list.length ? '' : '<div class="muted">No notes yet. They are saved and survive a restart.</div>') +
        list.map(function (n, i) {
          return '<div class="row"><div class="grow">' + esc(n.text) + '<div class="muted">' + new Date(n.at).toLocaleString() + '</div></div>' +
            '<button class="btn" data-del="' + i + '">Delete</button></div>';
        }).join('') + '</div></div>';
      var input = document.getElementById('note');
      function add() {
        var text = input.value.trim();
        if (!text) return;
        list.unshift({ text: text, at: Date.now() });
        save({ notes: list.slice(0, 50) }).then(notes);
      }
      document.getElementById('addNote').onclick = add;
      input.onkeydown = function (e) { if (e.key === 'Enter') add(); };
      Array.prototype.forEach.call(panel.querySelectorAll('[data-del]'), function (b) {
        b.onclick = function () { list.splice(Number(b.getAttribute('data-del')), 1); save({ notes: list }).then(notes); };
      });
      fit();
    });
  }

  /* ------------------------------------------------ timer */

  var sw = { running: false, start: 0, total: 0, laps: [], tick: 0 };
  function fmt(ms) {
    var s = Math.floor(ms / 1000), m = Math.floor(s / 60), h = Math.floor(m / 60);
    var cs = Math.floor((ms % 1000) / 10);
    return (h ? h + ':' : '') + String(m % 60).padStart(2, '0') + ':' + String(s % 60).padStart(2, '0') + '.' + String(cs).padStart(2, '0');
  }
  function elapsed() { return sw.total + (sw.running ? Date.now() - sw.start : 0); }
  function timer() {
    panel.innerHTML =
      '<div class="card" style="text-align:center"><div class="big" id="clock">' + fmt(elapsed()) + '</div>' +
      '<div style="display:flex;gap:6px;justify-content:center;margin-top:8px">' +
      '<button class="btn primary" id="go">' + (sw.running ? 'Pause' : 'Start') + '</button>' +
      '<button class="btn" id="lap">Lap</button><button class="btn" id="reset">Reset</button></div>' +
      '<div style="margin-top:8px;text-align:left">' + sw.laps.map(function (l, i) {
        return '<div class="row"><div class="grow">Lap ' + (sw.laps.length - i) + '</div><div style="font-variant-numeric:tabular-nums">' + fmt(l) + '</div></div>';
      }).join('') + '</div></div>';
    document.getElementById('go').onclick = function () {
      if (sw.running) { sw.total = elapsed(); sw.running = false; } else { sw.start = Date.now(); sw.running = true; }
      timer();
    };
    document.getElementById('lap').onclick = function () { sw.laps.unshift(elapsed()); timer(); };
    document.getElementById('reset').onclick = function () { sw = { running: false, start: 0, total: 0, laps: [], tick: sw.tick }; timer(); };
    clearInterval(sw.tick);
    sw.tick = setInterval(function () {
      var el = document.getElementById('clock');
      if (!el) { clearInterval(sw.tick); return; }
      el.textContent = fmt(elapsed());
    }, 50);
    fit();
  }

  /* ------------------------------------------------ dice and teams */

  function dice() {
    panel.innerHTML =
      '<div class="card"><div style="font-weight:700;margin-bottom:6px">Roll</div>' +
      '<div style="display:flex;align-items:center;gap:10px"><div class="big" id="roll">-</div><div class="grow"></div>' +
      [6, 10, 20, 100].map(function (n) { return '<button class="btn" data-d="' + n + '">d' + n + '</button>'; }).join('') + '</div></div>' +
      '<div class="card" style="margin-top:8px"><div style="font-weight:700;margin-bottom:6px">Random teams</div>' +
      '<textarea id="names" placeholder="One name per line"></textarea>' +
      '<div style="display:flex;gap:6px;margin-top:6px;align-items:center"><span class="muted">Teams</span>' +
      '<input id="count" type="number" min="2" max="8" value="2" style="width:70px"><div class="grow"></div>' +
      '<button class="btn primary" id="split">Split</button></div><div id="teams" style="margin-top:8px"></div></div>';
    Array.prototype.forEach.call(panel.querySelectorAll('[data-d]'), function (b) {
      b.onclick = function () {
        var n = Number(b.getAttribute('data-d')), el = document.getElementById('roll'), spins = 0;
        var t = setInterval(function () {
          el.textContent = 1 + Math.floor(Math.random() * n);
          if (++spins > 10) clearInterval(t);
        }, 45);
      };
    });
    document.getElementById('split').onclick = function () {
      var names = document.getElementById('names').value.split('\n').map(function (s) { return s.trim(); }).filter(Boolean);
      var count = Math.max(2, Math.min(8, Number(document.getElementById('count').value) || 2));
      for (var i = names.length - 1; i > 0; i--) { var j = Math.floor(Math.random() * (i + 1)); var tmp = names[i]; names[i] = names[j]; names[j] = tmp; }
      var teams = [];
      for (var k = 0; k < count; k++) teams.push([]);
      names.forEach(function (n, idx) { teams[idx % count].push(n); });
      document.getElementById('teams').innerHTML = names.length
        ? teams.map(function (t, idx) { return '<div class="row"><b style="width:64px">Team ' + (idx + 1) + '</b><div class="grow">' + esc(t.join(', ') || '-') + '</div></div>'; }).join('')
        : '<div class="muted">Add some names first.</div>';
      fit();
    };
    fit();
  }

  /* ------------------------------------------------ reaction game */

  var game = { state: 'idle', go: 0, wait: 0 };
  function reaction() {
    load().then(function (d) {
      var best = d.best || 0;
      panel.innerHTML =
        '<div class="card"><div class="game" id="pad" style="background:var(--bg-3,#1b2434)">Click to start</div>' +
        '<div style="display:flex;margin-top:8px"><div class="grow muted" id="last">Wait for the colour, then click as fast as you can.</div>' +
        '<div class="muted">Best: <b id="best">' + (best ? best + ' ms' : '-') + '</b></div></div></div>';
      var pad = document.getElementById('pad');
      function set(text, bg) { pad.textContent = text; pad.style.background = bg; }
      pad.onclick = function () {
        if (game.state === 'idle' || game.state === 'done') {
          game.state = 'waiting';
          set('Wait for it...', 'var(--bg-4,#253044)');
          game.wait = setTimeout(function () { game.state = 'go'; game.go = performance.now(); set('CLICK!', 'var(--accent,#6557f5)'); }, 900 + Math.random() * 2600);
        } else if (game.state === 'waiting') {
          clearTimeout(game.wait);
          game.state = 'done';
          set('Too early - click to try again', 'var(--bad,#ef5965)');
        } else if (game.state === 'go') {
          var ms = Math.round(performance.now() - game.go);
          game.state = 'done';
          set(ms + ' ms - click to go again', 'var(--good,#2ed98b)');
          document.getElementById('last').textContent = ms < 200 ? 'Lightning.' : ms < 300 ? 'Quick.' : 'Keep practising.';
          if (!best || ms < best) { best = ms; save({ best: ms }); document.getElementById('best').textContent = ms + ' ms'; pugo.notify('New best reaction time: ' + ms + ' ms'); }
        }
      };
      fit();
    });
  }

  /* ------------------------------------------------ live events */

  pugo.on('route', function (r) {
    events.route++;
    lastRoute = r;
  });

  pugo.setTitle('Pugo Toolkit');
  drawTabs();
  draw();
})();
`;
